import React, { useState } from "react";

import { ExternalProductOptionsSummary } from "features/MenuItemEditor/components/ExternalProductOptionsSummary";
import { ExternalProductSelect } from "features/MenuItemEditor/components/ExternalProductSelect";
import { BadgeChipsOrder } from "features/MenuItemEditor/components/BadgeChipsOrder";

import {
    FieldGrid,
    IconButton,
    LabeledField,
    LanguageSegmentedControl,
    SectionCard,
    Select,
    Stack,
    SyncIcon,
    TextInput,
    Textarea,
    ToggleSwitch,
} from "shared/ui";
import { syncIikoNomenclature } from "services/menu/rest";
import { ExternalProductDto, MenuCategoryDto } from "services/menu/restModels";
import {
    COOKING_TIME_OPTIONS,
    cookingTimeToValue,
    valueToCookingTime,
} from "shared/lib/cookingTime";
import { localizedText } from "shared/lib/localizedMap";
import { MEASUREMENT_UNIT_OPTIONS } from "shared/lib/measurement";
import { useActiveLanguage } from "shared/hooks/useActiveLanguage";

import { useMenuItemEditorContext } from "features/MenuItemEditor/context";

import {
    ConnectedBadge,
    Divider,
    IikoRow,
    IikoSelectWrap,
    NutritionLabel,
    WeightBlock,
    WeightRow,
} from "./styles";

const cookingCategoryHint = (
    parentCategory: MenuCategoryDto | undefined,
    locale: string
): string => {
    const cookingTime = parentCategory?.cookingTime;
    if (parentCategory && cookingTime?.fromTime) {
        return `У категории «${localizedText(parentCategory.name, locale)}» — ${cookingTime.fromTime}–${cookingTime.toTime} минут`;
    }
    return "Время приготовления не задано";
};

export const MainTab = () => {
    const state = useMenuItemEditorContext();
    const { locale } = state;
    const { activeLang, setActiveLang } = useActiveLanguage(locale, state.availableLanguages);

    const [syncing, setSyncing] = useState<boolean>(false);

    // Та же ручка, что и у страницы интеграции: номенклатуру подтягивают из обоих мест.
    const handleSync = async () => {
        setSyncing(true);
        try {
            await syncIikoNomenclature(state.menuId);
            state.notify.showSuccess("Номенклатура синхронизирована");
        } catch (error) {
            state.notify.showError("Не удалось синхронизировать номенклатуру", error);
        } finally {
            setSyncing(false);
        }
    };

    return (
        <>
            <SectionCard
                title="О позиции"
                headerRight={
                    state.availableLanguages.length > 0 ? (
                        <LanguageSegmentedControl
                            languages={state.availableLanguages}
                            activeLang={activeLang}
                            primaryLang={locale}
                            onChange={setActiveLang}
                            isMissing={(lang) => !state.name[lang]}
                        />
                    ) : undefined
                }
            >
                <Stack>
                    <LabeledField label="Название">
                        <TextInput
                            value={state.name[activeLang]}
                            onChange={(value) => state.setLocalizedName(value, activeLang)}
                            placeholder="Например, Том ям с креветками"
                        />
                    </LabeledField>
                    <LabeledField
                        label="Описание"
                        hint="Гость видит описание в карточке позиции"
                    >
                        <Textarea
                            value={state.description[activeLang]}
                            onChange={(value) => state.setLocalizedDescription(value, activeLang)}
                            placeholder="Состав, вкус, особенности подачи"
                            rows={4}
                        />
                    </LabeledField>
                    <LabeledField label="Категория">
                        <Select
                            value={state.menuCategoryId}
                            onChange={(value) => state.setMenuCategoryId(Number(value))}
                            options={state.categories.map((category) => ({
                                value: category.id,
                                label: localizedText(category.name, locale),
                            }))}
                        />
                    </LabeledField>
                </Stack>
            </SectionCard>

            <SectionCard title="Цена и продажа">
                <FieldGrid>
                    <LabeledField label="Цена">
                        <TextInput
                            value={state.priceToShow}
                            onChange={state.setPriceWrapper}
                            suffix={state.currencyShortName}
                        />
                    </LabeledField>
                    <LabeledField
                        label="Зачёркнутая цена"
                        hint="Отображается рядом с основной"
                    >
                        <TextInput
                            value={state.oldPriceToShow}
                            onChange={state.setOldPriceWrapper}
                            suffix={state.currencyShortName}
                        />
                    </LabeledField>
                </FieldGrid>
                <Divider>
                    <ToggleSwitch
                        checked={state.isInStopList}
                        onChange={state.setIsInStopList}
                        label="В стоп-листе"
                        hint={
                            state.isInStopList
                                ? "Позиция скрыта из меню, пока включено"
                                : "Позиция продаётся и видна гостям"
                        }
                    />
                </Divider>
            </SectionCard>

            <SectionCard title="Вес и КБЖУ">
                <WeightBlock>
                    <LabeledField label="Вес / объём">
                        <WeightRow>
                            <TextInput value={state.weight} onChange={state.setWeight} />
                            <Select
                                value={state.measurement}
                                onChange={state.setMeasurement}
                                options={MEASUREMENT_UNIT_OPTIONS}
                            />
                        </WeightRow>
                    </LabeledField>
                </WeightBlock>
                <NutritionLabel>на 100 г</NutritionLabel>
                <FieldGrid $min={140}>
                    <LabeledField label="Килокалории">
                        <TextInput
                            value={state.caloriesToShow}
                            onChange={state.setCaloriesHandler}
                        />
                    </LabeledField>
                    <LabeledField label="Белки">
                        <TextInput
                            value={state.proteinsToShow}
                            onChange={state.setProteinsHandler}
                        />
                    </LabeledField>
                    <LabeledField label="Жиры">
                        <TextInput value={state.fatsToShow} onChange={state.setFatsHandler} />
                    </LabeledField>
                    <LabeledField label="Углеводы">
                        <TextInput
                            value={state.carbohydratesToShow}
                            onChange={state.setCarbohydratesHandler}
                        />
                    </LabeledField>
                </FieldGrid>
            </SectionCard>

            <SectionCard title="Время приготовления">
                <Stack $gap={14}>
                    <ToggleSwitch
                        checked={state.cookingTimeFromCategory}
                        onChange={state.setCookingTimeFromCategory}
                        disabled={state.individualCookingTime}
                        label="Как у всей категории"
                        hint={cookingCategoryHint(state.parentCategory, locale)}
                    />
                    <ToggleSwitch
                        checked={state.individualCookingTime}
                        onChange={state.setIndividualCookingTime}
                        disabled={state.cookingTimeFromCategory}
                        label="Индивидуально для блюда"
                        hint="Гость увидит это время в корзине"
                    />
                    {state.individualCookingTime && (
                        <Select
                            value={cookingTimeToValue(state.itemCookingTime)}
                            options={COOKING_TIME_OPTIONS}
                            onChange={(value) =>
                                state.setItemCookingTime(valueToCookingTime(value))
                            }
                        />
                    )}
                </Stack>
            </SectionCard>

            <SectionCard
                title="Бейджи позиции"
                description="Плашки на карточке в меню. Порядок чипов — порядок плашек у гостя"
            >
                <BadgeChipsOrder />
            </SectionCard>

            {state.hasIikoIntegration && (
                <SectionCard
                    title="Интеграция с iiko"
                    headerRight={<ConnectedBadge>Подключена</ConnectedBadge>}
                >
                    <Stack $gap={12}>
                        <IikoRow>
                            <IikoSelectWrap>
                                <ExternalProductSelect
                                    menuId={state.menuId}
                                    value={state.selectedExternalProduct}
                                    onChange={(product: ExternalProductDto | null) => {
                                        state.setSelectedExternalProduct(product);
                                        state.setExternalId(product?.id ?? null);
                                    }}
                                    label="Товар в iiko"
                                    initialExternalId={state.externalId}
                                />
                            </IikoSelectWrap>
                            <IconButton
                                label="Синхронизировать номенклатуру"
                                variant="outlined"
                                size="large"
                                disabled={syncing}
                                onClick={handleSync}
                            >
                                <SyncIcon />
                            </IconButton>
                        </IikoRow>
                        <ExternalProductOptionsSummary
                            menuId={state.menuId}
                            externalProductId={state.selectedExternalProduct?.id ?? null}
                        />
                    </Stack>
                </SectionCard>
            )}
        </>
    );
};
